import {
  ButtonHTMLAttributes,
  Children,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  createContext,
  isValidElement,
  useContext,
  useMemo,
} from 'react';

import { Styles, css, cx, sva } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';

import { IsomorphicLink, IsomorphicLinkProps } from '../../design-utils/isomorphic-link';
import CautionCircleIcon from '../../icon/generated/CautionCircleIcon';
import CheckIcon from '../../icon/generated/CheckIcon';
import ChevronRightIcon from '../../icon/generated/ChevronRightIcon';
import { flattenFragment } from '../../misc/node/fragment';
import { TriggerChip, TriggerChipProps } from '../chip/trigger-chip';
import { Content, DropdownRootProps, Root, Trigger, TriggerProps, useDropdown } from './dropdown';

interface BaseItemProps<V extends string> {
  icon?: ReactNode;
  label: ReactNode;
  value: V;
}

interface SelectProps<V extends string> {
  selected?: V;
  onChange?: (value: V) => void;
}

const SelectContext = createContext<(SelectProps<string> & { items: BaseItemProps<string>[] }) | undefined>(undefined);

const useSelectContext = () => {
  const ctx = useContext(SelectContext);
  if (!ctx) throw new Error('Select must be used within <Select> context');
  return ctx;
};

// Helper function to extract items with type guards
const extractSelectItems = <V extends string>(children: ReactNode): BaseItemProps<V>[] => {
  const selectComponent = Children.toArray(children).find(
    (c): c is ReactElement => isValidElement(c) && c.type === Select,
  );
  if (!selectComponent) return [];

  return flattenFragment((selectComponent as ReactElement<{ children: ReactNode }>).props.children)
    .filter((c): c is ReactElement<BaseItemProps<string>> => isValidElement(c) && c.type === Item)
    .map((item) => ({ icon: item.props.icon, label: item.props.label, value: item.props.value as V }));
};

const DropdownSelectRoot = <V extends string>({
  selected,
  onChange,
  children,
  ...dropdownProps
}: SelectProps<V> & DropdownRootProps) => {
  const items = selected ? extractSelectItems(children) : [];

  return (
    <SelectContext
      value={{
        selected: selected as string,
        onChange: onChange as (value: string) => void,
        items,
      }}
    >
      <Root {...dropdownProps}>{children}</Root>
    </SelectContext>
  );
};

const DropdownSelectTriggerChip = (props: TriggerChipProps) => {
  const { open } = useDropdown();
  const { selected, items } = useSelectContext();

  return (
    <Trigger asChild>
      <TriggerChip open={open} selected={!!selected} {...items.find((i) => i.value === selected)} {...props} />
    </Trigger>
  );
};
DropdownSelectTriggerChip.displayName = 'DropdownSelect.TriggerChip';

const Select = ({ portal, css: cssProps, children }: PropsWithChildren<{ portal?: boolean; css?: Styles }>) => {
  return (
    <Content portal={portal} css={css.raw(stack.raw({ p: 4, gap: 2, maxH: 228, overflowY: 'scroll' }), cssProps)}>
      {children}
    </Content>
  );
};

interface BaseSelectItemProps<V extends string> extends BaseItemProps<V> {
  status?: 'base' | 'warning' | 'error';
  subLabel?: ReactNode;
  onClick?: (value: V) => void;
}

type ButtonElementProps<V extends string> = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof BaseSelectItemProps<V> | 'className' | 'children'
>;
type LinkElementProps<V extends string> = Omit<
  IsomorphicLinkProps,
  keyof BaseSelectItemProps<V> | 'className' | 'children'
>;
type SelectItemProps<V extends string, T extends 'button' | 'a' | undefined = 'button'> = {
  as?: T;
} & (T extends 'button' ? ButtonElementProps<V> : LinkElementProps<V>) &
  BaseSelectItemProps<V>;

const Item = <V extends string, T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  status = 'base',
  value,
  icon,
  label,
  subLabel,
  onClick,
  ...restProps
}: SelectItemProps<V, T>) => {
  const { setOpen } = useDropdown();
  const { selected, onChange } = useSelectContext();

  const recipe = useMemo(() => selectItemRecipes.raw({ status }), [status]);

  if (as === 'button') {
    const isSelectedItem = selected === value;
    return (
      <button
        type='button'
        role='menuitem'
        // oxlint-disable-next-line role-supports-aria-props
        aria-selected={isSelectedItem}
        data-value={value}
        className={cx('group', css(recipe.item))}
        onClick={() => {
          onChange?.(value);
          onClick?.(value);
          setOpen(false);
        }}
        {...(restProps as ButtonElementProps<V>)}
      >
        {status === 'base' && icon && <div className={css(recipe.prefixIcon)}>{icon}</div>}
        {status !== 'base' && (
          <div className={css(recipe.prefixIcon)}>
            <CautionCircleIcon />
          </div>
        )}
        <div>
          {label}
          {subLabel && <div className={css(recipe.subLabel)}>{subLabel}</div>}
        </div>
        {isSelectedItem && (
          <div className={css(recipe.suffixIcon)}>
            <CheckIcon />
          </div>
        )}
      </button>
    );
  }
  if (as === 'a') {
    return (
      <IsomorphicLink
        role='menuitem'
        data-value={value}
        className={css(recipe.item)}
        onClick={() => {
          onChange?.(value);
          onClick?.(value);
          setOpen(false);
        }}
        {...(restProps as LinkElementProps<V>)}
      >
        {status === 'base' && icon && <div className={css(recipe.prefixIcon)}>{icon}</div>}
        {status !== 'base' && (
          <div className={css(recipe.prefixIcon)}>
            <CautionCircleIcon />
          </div>
        )}
        <div>
          {label}
          {subLabel && <div className={css(recipe.subLabel)}>{subLabel}</div>}
        </div>
        <div className={css(recipe.suffixIcon)}>
          <ChevronRightIcon />
        </div>
      </IsomorphicLink>
    );
  }

  return null;
};

DropdownSelectRoot.displayName = 'DropdownSelect.Root';
DropdownSelectTriggerChip.displayName = 'DropdownSelect.TriggerChip';
Select.displayName = 'DropdownSelect.Select';
Item.displayName = 'DropdownSelect.Item';

type DropdownSelectComponent<V extends string> = {
  Root: (props: SelectProps<V> & DropdownRootProps) => ReactElement | null;
  Trigger: (props: TriggerProps) => ReactElement | null;
  TriggerChip: (props: TriggerChipProps) => ReactElement | null;
  Select: (props: PropsWithChildren<{ portal?: boolean; css?: Styles }>) => ReactElement | null;
  Item: <V extends string, T extends 'button' | 'a' | undefined = 'button'>(
    props: SelectItemProps<V, T>,
  ) => ReactElement | null;
};

const DropdownSelect = {
  Root: DropdownSelectRoot,
  Trigger,
  TriggerChip: DropdownSelectTriggerChip,
  Select,
  Item,
};

export function createDropdownSelect<V extends string>() {
  return DropdownSelect as unknown as DropdownSelectComponent<V>;
}

const selectItemRecipes = sva({
  slots: ['item', 'prefixIcon', 'subLabel', 'suffixIcon'],
  base: {
    item: {
      ...hstack.raw({
        pos: 'relative',
        textStyle: 'Text/M/Regular',
        textAlign: 'left',
        color: 'content.default.level-5',
        flexShrink: 0,
        minH: 32,
        pl: 10,
        pr: 40,
        py: 6,
        rounded: 4,
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
        _hover: { bgColor: 'background.interaction.hover' },
        _disabled: { color: 'content.interaction.disabled' },
        _focusVisible: {
          outlineStyle: 'solid',
          outlineWidth: 2,
          outlineColor: 'border.accent.info',
        },
      }),
    },
    subLabel: {
      textStyle: 'Text/S/Regular',
    },
    prefixIcon: {
      ...center.raw({
        mr: 8,
        '& > svg': { w: 16, h: 16 },
        '& img': {
          w: 16,
          h: 16,
          _groupDisabled: {
            opacity: 0.3,
          },
        },
      }),
    },
    suffixIcon: {
      ...center.raw({
        pos: 'absolute',
        top: 0,
        right: 8,
        bottom: 0,
        '& > svg': { w: 16, h: 16 },
      }),
    },
  },
  variants: {
    status: {
      base: {
        subLabel: { color: 'content.default.level-2' },
      },
      warning: {
        prefixIcon: { color: 'content.state.warning' },
        subLabel: { color: 'content.state.warning' },
      },
      error: {
        prefixIcon: { color: 'content.state.negative' },
        subLabel: { color: 'content.state.negative' },
      },
    },
  },
});
